// src/services/session-logger.service.js

const STORAGE_KEY = 'forum_session_logs_v1';

const DEFAULT_MAX_SESSIONS = 3;
const DEFAULT_MAX_ENTRIES = 2000;

const MAX_TEXT_LENGTH = 500;
const MAX_GENERIC_STRING_LENGTH = 1000;

function readStorage() {
    try {
        return JSON.parse(localStorage.getItem(STORAGE_KEY)) || [];
    } catch {
        return [];
    }
}

function writeStorage(value) {
    try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(value));
        return true;
    } catch (error) {
        console.warn('[SessionLogger] localStorage write failed', error);
        return false;
    }
}

function trimText(text, maxLength = MAX_TEXT_LENGTH) {
    if (typeof text !== 'string') return text;

    if (text.length <= maxLength) return text;

    return `${text.slice(0, maxLength)}…[trimmed ${text.length - maxLength}]`;
}

function safeClone(value) {
    try {
        return JSON.parse(JSON.stringify(value));
    } catch {
        return '[unserializable]';
    }
}

function sanitizeError(error) {
    if (!error) return null;

    return {
        message: error.message,
        name: error.name,
        type: error.type,
        description: error.description,
        data: safeClone(error.data),
        cause: error.cause
            ? {
                message: error.cause.message,
                name: error.cause.name,
            }
            : undefined,
    };
}

function sanitizeGenericPayload(payload) {
    const cloned = safeClone(payload);

    return trimLargeStrings(cloned);
}

function trimLargeStrings(value) {
    if (typeof value === 'string') {
        return trimText(value, MAX_GENERIC_STRING_LENGTH);
    }

    if (Array.isArray(value)) {
        return value.map(item => trimLargeStrings(item));
    }

    if (value && typeof value === 'object') {
        return Object.keys(value).reduce((acc, key) => {
            const lowerKey = key.toLowerCase();

            if (
                lowerKey.includes('audio') ||
                lowerKey.includes('buffer') ||
                lowerKey.includes('blob') ||
                lowerKey.includes('base64')
            ) {
                acc[key] = '[removed-large-binary-like-field]';
            } else {
                acc[key] = trimLargeStrings(value[key]);
            }

            return acc;
        }, {});
    }

    return value;
}

function sanitizePayload(event, payload) {
    if (event === 'tts') {
        const data = Array.isArray(payload) ? payload[0] : payload;

        return {
            message_id: data?.message_id,
            hasAudio: Boolean(data?.audio),
            audioSize: data?.audio?.byteLength || data?.audio?.length || null,
        };
    }

    if (event === 'message') {
        const data = Array.isArray(payload) ? payload[0] : payload;

        return {
            message_id: data?.message_id,
            is_final: data?.is_final,
            original: {
                text: trimText(data?.original?.text),
            },
            concat: trimText(data?.concat),
            sender: data?.sender
                ? {
                    language: data.sender.language,
                    name: data.sender.name,
                    sender_id: data.sender.sender_id,
                }
                : null,
            translations: data?.translations
                ? Object.keys(data.translations)
                : undefined,
            start_time: data?.start_time,
            end_time: data?.end_time,
            asr_segment: data?.asr_segment
                ? trimLargeStrings(data.asr_segment)
                : undefined,
        };
    }

    if (event === 'connect_error') {
        const error = Array.isArray(payload) ? payload[0] : payload;
        return sanitizeError(error);
    }

    return sanitizeGenericPayload(payload);
}

export function createSessionLogger({
                                        roomCode,
                                        userId,
                                        maxSessions = DEFAULT_MAX_SESSIONS,
                                        maxEntries = DEFAULT_MAX_ENTRIES,
                                    }) {
    const normalizedRoomCode = roomCode || 'unknown-room';

    function getSessions() {
        return readStorage();
    }

    function saveSessions(sessions) {
        const sorted = sessions
            .sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime())
            .slice(0, maxSessions);

        const saved = writeStorage(sorted);

        /**
         * Если localStorage переполнен, пробуем сохранить только последние половину entries
         * в текущей сессии. Это лучше, чем полностью потерять лог.
         */
        if (!saved) {
            const compacted = sorted.map(session => {
                if (session.roomCode !== normalizedRoomCode) {
                    return session;
                }

                return {
                    ...session,
                    entries: session.entries.slice(-Math.floor(maxEntries / 2)),
                    compactedAt: new Date().toISOString(),
                };
            });

            writeStorage(compacted);
        }
    }

    function getSession(sessions) {
        let session = sessions.find(item => item.roomCode === normalizedRoomCode);

        if (!session) {
            session = {
                roomCode: normalizedRoomCode,
                userId,
                startedAt: new Date().toISOString(),
                updatedAt: new Date().toISOString(),
                entries: [],
            };

            sessions.unshift(session);
        }

        return session;
    }

    function log(type, event, payload = null, meta = null) {
        const sessions = getSessions();
        const session = getSession(sessions);

        session.entries.push({
            id: `${Date.now()}-${Math.random().toString(16).slice(2)}`,
            timestamp: new Date().toISOString(),
            type,
            event,
            payload: sanitizePayload(event, payload),
            meta: sanitizeGenericPayload(meta),
        });

        if (session.entries.length > maxEntries) {
            session.entries = session.entries.slice(-maxEntries);
        }

        session.updatedAt = new Date().toISOString();

        saveSessions(sessions);
    }

    function getCurrentSession() {
        return getSessions().find(item => item.roomCode === normalizedRoomCode) || null;
    }

    function exportSession() {
        const session = getCurrentSession();

        if (!session) {
            console.warn(`[SessionLogger] No logs found for roomCode "${normalizedRoomCode}"`);
            return false;
        }

        const blob = new Blob([JSON.stringify(session, null, 2)], {
            type: 'application/json',
        });

        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');

        link.href = url;
        link.download = `session-${normalizedRoomCode}-logs.json`;

        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        URL.revokeObjectURL(url);

        return true;
    }

    function clearSession() {
        saveSessions(getSessions().filter(item => item.roomCode !== normalizedRoomCode));
    }

    function clearAllSessions() {
        writeStorage([]);
    }

    return {
        log,
        exportSession,
        clearSession,
        clearAllSessions,
        getSessions,
        getCurrentSession,
    };
}
